import React from "react";

const WinnerBanner = ({ result }) => {
  if (!result) return null;

  const isTie = result.overall === "tie";
  const winnerName = result.overall === "A" ? result.nameA : result.nameB;
  const winnerScore = result.overall === "A" ? result.scoreA : result.scoreB;
  const loserScore = result.overall === "A" ? result.scoreB : result.scoreA;

  const bgGradient = isTie
    ? "from-gray-500 to-gray-600"
    : result.overall === "A"
    ? "from-blue-500 to-blue-700"
    : "from-emerald-500 to-emerald-700";

  return (
    <div className="bg-white rounded-2xl shadow border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className={`bg-gradient-to-r ${bgGradient} px-6 py-5 flex items-center gap-4`}>
        <div className="text-4xl">{isTie ? "🤝" : "🏆"}</div>
        <div className="flex-1">
          {isTie ? (
            <p className="text-lg font-bold text-white">It's a Draw!</p>
          ) : (
            <>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/70 mb-0.5">Winner</p>
              <p className="text-xl font-bold text-white">{winnerName}</p>
            </>
          )}
          <p className="text-sm text-white/80 mt-1">
            {isTie
              ? result.reason
              : `Wins ${winnerScore} out of ${result.metrics.length} metrics · ${loserScore} won by the other`}
          </p>
        </div>
        {!isTie && (
          <div className="text-right bg-white/10 rounded-xl px-4 py-2">
            <div className="text-3xl font-black text-white">
              {winnerScore}<span className="text-base font-normal text-white/60">/{result.metrics.length}</span>
            </div>
            <div className="text-xs text-white/60">metrics won</div>
          </div>
        )}
      </div>

      {/* Why they won */}
      {!isTie && (
        <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 flex items-start gap-2">
          <span className="text-amber-500 mt-0.5">💡</span>
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Why {winnerName} wins: </span>
            {result.reason}.
          </p>
        </div>
      )}

      {/* Metric breakdown */}
      <div className="px-6 py-5">
        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Metric Breakdown</p>

        {/* Column headers */}
        <div className="grid grid-cols-[1fr_180px_1fr] mb-2 px-4">
          <div className="text-right">
            <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{result.nameA}</span>
          </div>
          <div className="text-center" />
          <div className="text-left">
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{result.nameB}</span>
          </div>
        </div>

        <div className="space-y-2">
          {result.metrics.map((m) => {
            const isTieRow = m.winner === "tie";
            const aWins = m.winner === "A";
            const bWins = m.winner === "B";

            return (
              <div
                key={m.key}
                className={`grid grid-cols-[1fr_180px_1fr] items-center gap-2 rounded-xl px-4 py-3 ${
                  isTieRow ? "bg-gray-50 border border-gray-100"
                  : aWins ? "bg-blue-50 border border-blue-100"
                  : "bg-emerald-50 border border-emerald-100"
                }`}
              >
                {/* A value */}
                <div className="text-right flex items-center justify-end gap-2">
                  {aWins && <span className="text-xs font-bold text-white bg-blue-500 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">✓</span>}
                  <span className={`text-sm font-bold ${aWins ? "text-blue-700" : "text-gray-400"}`}>
                    {m.format(m.a)}
                  </span>
                </div>

                {/* Label */}
                <div className="text-center px-2">
                  <p className="text-xs font-semibold text-gray-600 leading-tight">{m.label}</p>
                  {isTieRow && <span className="text-xs text-gray-400">— Tie —</span>}
                </div>

                {/* B value */}
                <div className="text-left flex items-center gap-2">
                  <span className={`text-sm font-bold ${bWins ? "text-emerald-700" : "text-gray-400"}`}>
                    {m.format(m.b)}
                  </span>
                  {bWins && <span className="text-xs font-bold text-white bg-emerald-500 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">✓</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Score summary */}
        <div className="mt-5 flex items-center justify-center gap-8 py-4 border-t border-gray-100">
          <div className="text-center">
            <div className="text-3xl font-black text-blue-600">{result.scoreA}</div>
            <div className="text-xs text-gray-500 mt-1 max-w-[120px] truncate font-medium">{result.nameA}</div>
          </div>
          <div className="text-xl font-black text-gray-200">vs</div>
          <div className="text-center">
            <div className="text-3xl font-black text-emerald-600">{result.scoreB}</div>
            <div className="text-xs text-gray-500 mt-1 max-w-[120px] truncate font-medium">{result.nameB}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WinnerBanner;
